import { DefaultValues, FieldValues, Path, PathValue, useForm } from 'react-hook-form';
import RegisteredSiteDto from './dtos/RegisteredSiteDto';
import SiteAssignmentsDto, { SiteAssignmentDto } from './dtos/SiteAssignmentsDto';
import useDisplayMessage from './useDisplayMessage';
import { Query, QueryOptions } from './useQuery';
import { Request, RequestOptions } from './useRequest';
import useGetSitesNonAdmin from '../Requests/useGetSitesNonAdmin';

interface EntityFormConfig<FormType extends FieldValues, UpsertDto> {
  initialId?: string;
  defaultValues: DefaultValues<FormType>;
  labelSpace?: number;
  inputSpace?: number;
  useSiteAssignment: (options: QueryOptions<SiteAssignmentsDto>) => Query<SiteAssignmentsDto>;
  useUpsert: (options: RequestOptions<string>) => Request<string, UpsertDto>;
  toDto: (form: FormType) => UpsertDto;
  onSuccess: (entityId: string, form: FormType) => void;
}

const toAssignments = (sites: RegisteredSiteDto[]): SiteAssignmentDto[] =>
  sites.map(site => ({ siteId: site.id, siteName: site.name, assigned: false }));

const sortedByName = (sites: SiteAssignmentDto[]): SiteAssignmentDto[] =>
  [...sites].sort((a, b) => a.siteName.localeCompare(b.siteName));

const useEntityForm = <FormType extends FieldValues, UpsertDto>(
  config: EntityFormConfig<FormType, UpsertDto>) => {
  const displayMessage = useDisplayMessage();

  const { register, handleSubmit, control, getValues, setValue, trigger, watch, formState: { errors } } =
    useForm<FormType>({ defaultValues: config.defaultValues });

  const isCreating = !config.initialId;

  const getSiteAssignments = config.useSiteAssignment({
    autoQuery: !isCreating,
    onSuccess: ({ assignments }) => setValue(
      'siteIds' as Path<FormType>,
      assignments.filter(a => a.assigned).map(a => a.siteId) as unknown as PathValue<FormType, Path<FormType>>),
  });

  const getSites = useGetSitesNonAdmin();

  const upsert = config.useUpsert({
    onSuccess: (response) => config.onSuccess(config.initialId ?? response, getValues()),
    onError: (e) => displayMessage.fail(e.message),
  });

  const submit = handleSubmit(form => upsert.request(config.toDto(form)));

  const hookProps = (name: Path<FormType>) => ({
    labelSpace: config.labelSpace ?? 4,
    inputSpace: config.inputSpace ?? 7,
    name: name,
    setValue,
    register,
    errors,
  });

  return {
    hookProps,
    control,
    getValues,
    setValue,
    trigger,
    watch,
    submit,
    isSaving: upsert.isLoading && !upsert.isError,
    loading: getSites.isLoading || getSiteAssignments.isLoading,
    loadError: getSites.error ?? getSiteAssignments.error,
    displayMessage,
    sites: sortedByName(toAssignments(getSites.data ?? [])),
  };
};

export default useEntityForm;
